import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Trans, useTranslation } from "react-i18next";
import PageHeader from "@/shared/components/layout/dashboard-layout/page-header.tsx";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/ui/components/ui/card";
import { Skeleton } from "@/ui/components/ui/skeleton";
import { useGetLocationSettings, useUpdateLocationSettings, type UpdateLocationSettingsInput } from "@/settings/api";
import { Button } from "@/ui/components/ui/button";
import { Input } from "@/ui/components/ui/input";
import { Label } from "@/ui/components/ui/label";
import { TimezoneSelect } from "@/ui/components/ui/timezone-select";

const LocationSettings = () => {
  const { t } = useTranslation("settings");
  const { id } = useParams<{ id: string }>();
  const { data: settingsData, isLoading } = useGetLocationSettings(
    id ? parseInt(id) : undefined
  );

  const location = settingsData?.data.data.locations?.[0];
  const organization = settingsData?.data.data.organization;

  const updateLocation = useUpdateLocationSettings();
  const queryClient = useQueryClient();

  const currentName = location?.name ?? "";
  // The location's own configured zone is the answer; null means "unset" and is
  // rendered as such below.
  const currentTz = location?.timezone ?? null;

  const [name, setName] = useState("");
  const [selectedTz, setSelectedTz] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  useEffect(() => {
    setName(currentName);
    setSelectedTz(currentTz);
    setNameError(null);
  }, [currentName, currentTz]);

  const dirtyName = name.trim() !== currentName;
  const dirtyTz = selectedTz !== currentTz;
  const canSave = ((dirtyName && name.trim().length > 0) || dirtyTz) && !updateLocation.isPending;

  const saveSettings = async () => {
    if (!location?.id) return;
    setNameError(null);
    // Send only dirty fields.
    const body: UpdateLocationSettingsInput = { id: location.id };
    if (dirtyName) body.name = name.trim();
    if (dirtyTz) body.timezone = selectedTz; // may be null to clear
    if (body.name === undefined && body.timezone === undefined) return;

    try {
      await updateLocation.mutateAsync(body);
      toast.success(t("location.saved"));
      // Refetch settings; a timezone change also rebuilds anything keyed on local
      // time — the queue cursor, local-day analytics and history.
      queryClient.invalidateQueries({ queryKey: ["settings"] });
      if (dirtyTz) {
        queryClient.invalidateQueries({ queryKey: ["alerts", "queue"] });
        queryClient.invalidateQueries({ queryKey: ["alerts", "analytics"] });
        queryClient.invalidateQueries({ queryKey: ["alerts", "history"] });
      }
    } catch (e) {
      const code = (e as { response?: { data?: { error?: { code?: string; detail?: string } } } })
        .response?.data?.error?.code;
      const detail = (e as { response?: { data?: { error?: { detail?: string } } } }).response?.data
        ?.error?.detail;
      if (code === "location_name_conflict") {
        setNameError(t("location.nameConflict"));
      } else if (code === "location_settings_unavailable") {
        toast.error(t("location.serviceBusy"));
      } else {
        toast.error(detail ?? t("location.saveFailed"));
      }
    }
  };

  const breadcrumbs = [
    {
      path: "/",
      breadcrumb: organization?.name || t("breadcrumbs.organization"),
    },
    {
      path: "/settings/location",
      breadcrumb: t("breadcrumbs.locationSettings"),
    },
  ];

  if (!id) {
    return (
      <div className="h-full">
        <PageHeader breadcrumbs={breadcrumbs} />
        <div className="p-6">
          <p className="text-muted-foreground">{t("location.selectPrompt")}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-full">
        <PageHeader breadcrumbs={breadcrumbs} />
        <div className="p-6 space-y-6 max-w-4xl">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!location) {
    return (
      <div className="h-full">
        <PageHeader breadcrumbs={breadcrumbs} />
        <div className="p-6">
          <p className="text-muted-foreground">{t("location.notFound")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full">
      <PageHeader breadcrumbs={breadcrumbs} />
      <div className="p-6 space-y-6 max-w-4xl">
        <div>
          <h1 className="text-2xl font-bold">{t("location.title", { name: location.name })}</h1>
          <p className="text-muted-foreground">
            {t("location.description", { name: location.name })}
          </p>
        </div>

        {/* Location Information */}
        <Card>
          <CardHeader>
            <CardTitle>{t("location.infoTitle")}</CardTitle>
            <CardDescription>{t("location.infoDescription")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="max-w-sm space-y-1.5">
              <Label htmlFor="location-name">{t("location.nameLabel")}</Label>
              <Input
                id="location-name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setNameError(null);
                }}
                placeholder={t("location.namePlaceholder")}
                aria-invalid={nameError ? true : undefined}
              />
              {nameError && <p className="text-xs text-destructive">{nameError}</p>}
            </div>

            <div className="max-w-sm space-y-1.5">
              <Label>{t("location.timezoneLabel")}</Label>
              <TimezoneSelect value={selectedTz} onChange={setSelectedTz} />
              <p className="text-xs text-muted-foreground">
                <Trans
                  t={t}
                  i18nKey="location.timezoneCurrent"
                  values={{ timezone: currentTz ?? t("location.timezoneUnset") }}
                  components={{ current: <span className="font-medium text-foreground" /> }}
                />
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Button onClick={saveSettings} disabled={!canSave}>
                {updateLocation.isPending ? t("location.saving") : t("location.save")}
              </Button>
              {selectedTz && selectedTz !== currentTz && (
                <Button variant="ghost" size="sm" onClick={() => setSelectedTz(currentTz)}>
                  {t("location.timezoneReset")}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Machines at this Location */}
        <Card>
          <CardHeader>
            <CardTitle>{t("location.machinesTitle")}</CardTitle>
            <CardDescription>
              {t("location.machinesDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {settingsData?.data.data.machines && settingsData.data.data.machines.length > 0 ? (
                <div className="space-y-2">
                  {settingsData.data.data.machines.map((machine) => (
                    <div
                      key={machine.id}
                      className="flex items-center justify-between p-3 border rounded-md"
                    >
                      <div>
                        <p className="font-medium">{machine.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {t("location.machineStatus", { status: machine.status })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t("location.noMachines")}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end gap-3">
          <Button variant="outline">{t("actions.cancel")}</Button>
          <Button>{t("actions.saveChanges")}</Button>
        </div>
      </div>
    </div>
  );
};

export default LocationSettings;
